import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  EluveLogo,
  Heading,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Tag,
  Text,
  Textarea,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { CreateTemplateForm } from './CreateTemplateDialog';
import { DeleteTemplateAlert } from './DeleteTemplateAlert';
import { TemplateSection } from './TemplateSection';
import { useDocumentTemplates } from './useDocumentTemplates';
import { useTemplateListState } from './useTemplateListState';

export type GenerateDocumentParams = {
  prompt?: string;
  templateId?: string;
  content?: string;
  instructions?: string;
  docType?: string;
};

export type GenerateDocumentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isGenerating: boolean;
  onGenerate: (params: GenerateDocumentParams) => void;
  templates: ReturnType<typeof useDocumentTemplates>;
};

export const GenerateDocumentDialog: React.FC<GenerateDocumentDialogProps> = ({
  open,
  onOpenChange,
  isGenerating,
  onGenerate,
  templates,
}) => {
  const { t } = useTranslation();
  const {
    createdByYou,
    createdByEluve,
    loading,
    hasData,
    refetch,
    deleteTemplate,
  } = templates;

  const {
    editingTemplate,
    setEditingTemplate,
    deletingTemplate,
    setDeletingTemplate,
    isDeleting,
    handleDeleteTemplate,
  } = useTemplateListState({ deleteTemplate, refetch });

  const [view, setView] = useState<'list' | 'template'>('list');
  const [prompt, setPrompt] = useState('');
  const [search, setSearch] = useState('');

  const matchesSearch = (t: { name: string }) =>
    t.name.toLowerCase().includes(search.trim().toLowerCase());
  const proseCreatedByYou = createdByYou
    .filter((t) => t.kind !== 'PDF_FORM')
    .filter(matchesSearch);
  const proseCreatedByEluve = createdByEluve
    .filter((t) => t.kind !== 'PDF_FORM')
    .filter(matchesSearch);

  const backToList = () => {
    setView('list');
    setEditingTemplate(null);
  };

  const handleGenerateFromPrompt = () => {
    if (!prompt.trim()) return;
    onGenerate({ prompt: prompt.trim() });
    setPrompt('');
    onOpenChange(false);
  };

  const onEdit = (t: (typeof createdByYou)[number]) => {
    setEditingTemplate({
      id: t.id,
      name: t.name,
      content: t.content ?? '',
      kind: t.kind,
      sourceFileId: t.sourceFileId,
    });
    setView('template');
  };

  const onSelect = (t: (typeof createdByYou)[number]) => {
    onGenerate({
      templateId: t.id,
      content: t.content ?? '',
      instructions: t.instructions ?? undefined,
      docType: t.docType ?? undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // This dialog stays mounted, so reset the subview on close — otherwise
        // reopening lands on the stale editor view instead of the list.
        if (!next) backToList();
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="4xl"
        className="max-h-[85vh] gap-0! overflow-hidden p-0!"
      >
        {view === 'template' ? (
          <>
            <DialogTitle className="sr-only">
              {editingTemplate
                ? t('appointmentTools.documents.editTemplateTitle')
                : t('appointmentTools.documents.createTemplateTitle')}
            </DialogTitle>
            <CreateTemplateForm
              template={editingTemplate}
              mode="note"
              onBack={backToList}
              onSaved={async () => {
                await refetch();
                backToList();
              }}
            />
          </>
        ) : (
          <Stack className="max-h-[85vh] overflow-hidden" align="stretch">
            <Stack gap={4} className="shrink-0 px-6 pt-6 pb-4">
              <Stack gap={1}>
                <DialogTitle asChild>
                  <Heading size="lg">
                    {t('appointmentTools.documents.createDocumentTitle')}
                  </Heading>
                </DialogTitle>
                <Text size="xs" color="tertiary">
                  {t('appointmentTools.documents.createDocumentSubtitle')}
                </Text>
              </Stack>

              <Stack
                gap={2}
                className="rounded-lg border border-border-indigo-subtle bg-bg-indigo-subtle-primary/40 p-4"
              >
                <Stack gap={1} direction="horizontal" align="center">
                  <Icon
                    name="Sparkles"
                    size="sm"
                    className="text-text-indigo"
                  />
                  <Text size="sm" weight="medium">
                    {t('appointmentTools.documents.generateWithAi')}
                  </Text>
                </Stack>
                <Text size="xs" color="tertiary">
                  {t('appointmentTools.documents.generateWithAiHint')}
                </Text>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={t(
                    'appointmentTools.documents.promptPlaceholder',
                  )}
                  className="min-h-[88px] resize-none bg-bg-primary focus-visible:ring-0"
                  disabled={isGenerating}
                />
                <Stack justify="end" direction="horizontal">
                  <Button
                    size="sm"
                    onClick={handleGenerateFromPrompt}
                    disabled={isGenerating || !prompt.trim()}
                  >
                    {isGenerating
                      ? t('appointmentTools.documents.generating')
                      : t('appointmentTools.documents.generate')}
                  </Button>
                </Stack>
              </Stack>
            </Stack>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-6">
              <Stack gap={3}>
                <Stack direction="horizontal" justify="between" align="center">
                  <Text size="md">
                    {t('appointmentTools.documents.templates')}
                  </Text>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t(
                      'appointmentTools.documents.searchTemplates',
                    )}
                    className="w-56"
                  />
                </Stack>

                <button
                  type="button"
                  onClick={() => {
                    setEditingTemplate(null);
                    setView('template');
                  }}
                  className="flex w-full items-center gap-3 rounded-lg border border-border-indigo-subtle bg-bg-indigo-subtle-primary/40 px-4 py-3 text-left transition-colors hover:bg-bg-indigo-subtle-primary/70"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-bg-indigo-strong-primary">
                    <Icon name="Plus" size="auto" className="text-white" />
                  </span>
                  <Stack gap={0}>
                    <Text size="sm" weight="medium">
                      {t('appointmentTools.documents.createNewTemplate')}
                    </Text>
                    <Text size="xs" color="tertiary">
                      {t('appointmentTools.documents.createNewTemplateHint')}
                    </Text>
                  </Stack>
                </button>

                {loading && !hasData ? (
                  <Stack
                    justify="center"
                    className="py-4"
                    direction="horizontal"
                  >
                    <LoadingIcon size="lg" />
                  </Stack>
                ) : (
                  <Stack gap={3}>
                    {proseCreatedByYou.length === 0 &&
                      proseCreatedByEluve.length === 0 && (
                        <Text size="xs" color="tertiary" className="py-3">
                          {t('appointmentTools.documents.noTemplates')}
                        </Text>
                      )}
                    <TemplateSection
                      variant="card"
                      title={t('appointmentTools.documents.createdByYou')}
                      badge={
                        <Icon
                          name="User"
                          size="xs"
                          className="text-text-tertiary"
                        />
                      }
                      templates={proseCreatedByYou}
                      isGenerating={isGenerating}
                      onSelect={onSelect}
                      onEdit={onEdit}
                      onDelete={(t) =>
                        setDeletingTemplate({ id: t.id, name: t.name })
                      }
                    />
                    <TemplateSection
                      variant="card"
                      title={t('appointmentTools.documents.createdByEluve')}
                      badge={<EluveLogo className="size-4" />}
                      tag={
                        <Tag
                          size="xs"
                          color="purple"
                          label={t('appointmentTools.documents.prebuilt')}
                        />
                      }
                      templates={proseCreatedByEluve}
                      isGenerating={isGenerating}
                      onSelect={onSelect}
                      onEdit={onEdit}
                      onDelete={(t) =>
                        setDeletingTemplate({ id: t.id, name: t.name })
                      }
                    />
                  </Stack>
                )}
              </Stack>
            </div>

            <DeleteTemplateAlert
              template={deletingTemplate}
              onOpenChange={(isOpen) => {
                if (!isOpen) setDeletingTemplate(null);
              }}
              onConfirm={handleDeleteTemplate}
              isDeleting={isDeleting}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
